/**
 * Label Layer Styles
 *
 * Region labels, fixed labels, and vignette ring layer factories.
 */

import type { LayerSpecification } from "maplibre-gl";

/**
 * Create MapLibre symbol layer for region labels.
 * Region labels are large area names (e.g. "Africa", "Asia") that:
 * - Stay horizontal relative to viewport (don't rotate with map)
 * - Have a soft halo for readability over varied backgrounds
 * - Always overlap/show (no collision hiding)
 * - Support optional sponsor logo above text via sponsorImageId property
 *
 * @param id - Layer ID
 * @param sourceId - GeoJSON source ID containing label features
 * @returns MapLibre symbol layer spec
 */
export function createRegionLabelLayer(
  id: string,
  sourceId: string
): LayerSpecification {
  return {
    id,
    type: "symbol",
    source: sourceId,
    filter: [
      "all",
      ["==", ["get", "type"], "region"],
      ["!=", ["get", "_visible"], false],
    ],
    layout: {
      "text-field": ["get", "text"],
      "text-size": ["get", "fontSize"],
      "text-font": [
        "case",
        ["==", ["get", "fontWeight"], "bold"],
        ["literal", ["Google Sans Flex Medium"]],
        ["literal", ["Google Sans Flex Regular"]],
      ],
      "text-rotation-alignment": "viewport",
      "text-pitch-alignment": "viewport",
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      // Sponsor logo: try image expression, empty string = no icon
      "icon-image": [
        "coalesce",
        ["image", ["get", "sponsorImageId"]],
        "",
      ],
      "icon-size": 0.7,
      "icon-anchor": "bottom",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      // Shift text down when sponsor logo present
      "text-offset": [
        "case",
        ["has", "sponsorImageId"],
        ["literal", [0, 0.6]],
        ["literal", [0, 0]],
      ],
    },
    paint: {
      "text-color": ["get", "color"],
      "text-opacity": ["get", "opacity"],
      "text-halo-color": [
        "coalesce",
        ["get", "backgroundColor"],
        "rgba(255,255,255,0.7)",
      ],
      "text-halo-width": ["coalesce", ["get", "haloWidth"], 2],
      "text-halo-blur": 1,
    },
  } as LayerSpecification;
}

/**
 * Create a single vignette ring layer.
 * Used for rendering individual gradient rings in the fog-of-war effect.
 *
 * Each ring is a fill layer with a specific opacity. Multiple rings with
 * graduated opacities create a smooth gradient from boundary to outer mask.
 *
 * @param id - Layer ID
 * @param sourceId - GeoJSON source with ring polygon (from createVignetteRings or createOuterMask)
 * @param maskColor - Color for the mask overlay (theme.colors.maskColor)
 * @param opacity - Ring opacity 0-1 (calculated based on ring position in gradient)
 * @returns MapLibre fill layer spec
 */
export function createVignetteRingLayer(
  id: string,
  sourceId: string,
  maskColor: string,
  opacity: number
): LayerSpecification {
  return {
    id,
    type: "fill",
    source: sourceId,
    paint: {
      "fill-color": maskColor,
      "fill-opacity": opacity,
    },
  } as LayerSpecification;
}

/**
 * Create MapLibre symbol layer for fixed labels.
 * Fixed labels are text that stays oriented to the map (rotates with it):
 * - Text aligned to map surface (rotates when map rotates)
 * - Custom rotation via rotation property
 * - White halo for readability
 * - Respects minZoom/maxZoom for visibility
 * - Support optional sponsor logo above text via sponsorImageId property
 *
 * @param id - Layer ID
 * @param sourceId - GeoJSON source ID containing label features
 * @returns MapLibre symbol layer spec
 */
export function createFixedLabelLayer(
  id: string,
  sourceId: string,
  zoomOffset?: number
): LayerSpecification {
  const zo = zoomOffset ?? 0;
  return {
    id,
    type: "symbol",
    source: sourceId,
    filter: [
      "all",
      ["==", ["get", "type"], "fixed"],
      ["!=", ["get", "_visible"], false],
    ],
    layout: {
      "text-field": ["get", "text"],
      "text-size": [
        "interpolate",
        ["exponential", 1.5],
        ["zoom"],
        10 + zo,
        ["*", ["get", "fontSize"], 0.25],
        14 + zo,
        ["*", ["get", "fontSize"], 0.5],
        18 + zo,
        ["get", "fontSize"],
        22 + zo,
        ["*", ["get", "fontSize"], 1.5],
      ],
      "text-font": [
        "case",
        ["==", ["get", "fontWeight"], "bold"],
        ["literal", ["Google Sans Flex Medium"]],
        ["literal", ["Google Sans Flex Regular"]],
      ],
      "text-rotation-alignment": "map",
      "text-pitch-alignment": "map",
      "text-rotate": ["get", "rotation"],
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      // Sponsor logo: try image expression, empty string = no icon
      "icon-image": [
        "coalesce",
        ["image", ["get", "sponsorImageId"]],
        "",
      ],
      // Scale icon with zoom (match text scaling behavior)
      "icon-size": [
        "interpolate",
        ["exponential", 1.5],
        ["zoom"],
        10 + zo,
        0.2,
        14 + zo,
        0.4,
        18 + zo,
        0.7,
        22 + zo,
        1.0,
      ],
      "icon-anchor": "bottom",
      "icon-rotation-alignment": "map",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      // Shift text down when sponsor logo present
      "text-offset": [
        "case",
        ["has", "sponsorImageId"],
        ["literal", [0, 0.8]],
        ["literal", [0, 0]],
      ],
    },
    paint: {
      "text-color": ["get", "color"],
      "text-opacity": ["get", "opacity"],
      "text-halo-color": "#ffffff",
      "text-halo-width": ["coalesce", ["get", "haloWidth"], 1.5],
    },
  } as LayerSpecification;
}
